import TeethDiagram from "./TeethDiagram";
import { useRef, useEffect } from 'react'
import { dentalMapping } from "../../utils/functions";
import { topViewDescription } from "../../utils/functions";
import { teethDescription } from "../../utils/functions";

export default function TeethDiagramInterractive({ isFdi = false, dentition = "permanent", existingData = [], selected = [], onMouseMove = (_) => { }, onMouseLeave = (_) => { }, onMouseEnter = (_) => { }, onClick = (_) => { } }) {
    const rootRegex = new RegExp("r\\d+");
    const diagramRef = useRef(null);

    const getRootId = (el) => {
        let identifier = undefined
        let rootFound = false;
        let classes = el.getAttribute("class").split(" ");
        let classesLength = classes.length;
        for (let i = 0; i < classesLength; i++) {
            const cl = classes[i].trim();
            if (rootRegex.test(cl)) {
                rootFound = true;
                identifier = cl
                break;
            }
        }
        if (!rootFound) {
            identifier = el.closest("g").getAttribute("class")
        }
        return identifier
    }

    useEffect(() => {
        if (!diagramRef.current)
            return;

        const currentItems = diagramRef.current.querySelectorAll(".selection,.planned,.complete");
        if (currentItems.length > 0) {
            [...currentItems].forEach(el => {
                el.classList.remove("selection")
                el.classList.remove("planned")
                el.classList.remove("complete")
                el.classList.remove("crown")
                el.classList.remove("active")
                el.classList.remove("extract")
            })
        }

        renderSelectedData()
        renderExistingData()
        // dentition belongs here too: switching charts mounts a different SVG,
        // which starts unpainted and has to be re-rendered from the same data.
    }, [selected, existingData, dentition]);

    const renderSelectedData = () => {
        if (selected) {
            selected.forEach(t => {
                let theClass = false;
                if (t.type == "root") {
                    theClass = "r" + t.id
                } else if (t.type == "number") {
                    theClass = "no" + t.id
                } else if (t.type == "surface") {
                    theClass = t.symbol + t.id
                } else if (t.type == "side") {
                    theClass = "t" + t.id
                }
                if (theClass) {
                    const item = diagramRef.current.getElementsByClassName(theClass)[0]
                    if (item) {
                        item.classList.add(t.style)
                    }
                }
            })
        }
    }
    const renderExistingData = () => {
        if (existingData && Array.isArray(existingData)) {
            existingData.forEach(e => {
                if (!Object.keys(dentalMapping).includes(e.style))
                    return;

                if (e.style == "extraction") {
                    ['i', 'o', 'd', 'f', 'l', 'm', 'r', 't', 'tv'].forEach(d => {
                        const item = diagramRef.current.getElementsByClassName(d + e.id)[0]

                        if (item) {
                            item.classList.add("extract")
                            item.classList.add(e.status === "Plan" ? "planned" : "complete")
                        }
                    })
                } else if (e.style == "implant") {
                    const item = diagramRef.current.getElementsByClassName("imp" + e.id)[0]
                    if (item) {
                        item.classList.add("active")
                        item.classList.add(e.status === "Plan" ? "planned" : "complete")
                    }
                } else if (e.style == "crown") {
                    const item = diagramRef.current.getElementsByClassName("t" + e.id)[0]
                    if (item) {
                        item.classList.add("crown")
                        item.classList.add(e.status === "Plan" ? "planned" : "complete")
                    }
                } else if (e.style == "side") {
                    const item = diagramRef.current.getElementsByClassName("t" + e.id)[0]
                    if (item) {
                        item.classList.add(e.status === "Plan" ? "planned" : "complete")
                    }
                } else if (e.style == "root") {
                    const item = diagramRef.current.getElementsByClassName("r" + e.id)[0]
                    if (item) {
                        item.classList.add(e.status === "Plan" ? "planned" : "complete")
                    }
                } else if (e.style == "surface") {
                    const item = diagramRef.current.getElementsByClassName(e.symbol + e.id)[0]
                    if (item) {
                        item.classList.add(e.status === "Plan" ? "planned" : "complete")
                    }
                } else if (e.type == "number") {
                    const item = diagramRef.current.getElementsByClassName("no" + e.id)[0]
                    // Guard like every branch above: records belonging to the other
                    // dentition have no element on the chart currently rendered.
                    if (item) {
                        item.classList.add(e.status === "Plan" ? "planned" : "complete")
                    }
                }
            })
        }
    }

    const handleEvent = (e, callBack, isClick = false) => {
        e.preventDefault()
        e.stopPropagation()
        const el = e.target;
        let type = "none"
        let identifier = undefined
        const isSurface = el.closest(".topView") !== null
        const isNumber = el.closest(".numbers") !== null
        const isRoot = el.closest(".roots") !== null
        const isSide = el.closest(".teeth") !== null
        if (isSurface) {
            if (el.classList.contains("cls-2")) {
                type = "surface"
                const topViewClasses = el.classList;
                [...topViewClasses].forEach(c => {
                    if (c.startsWith("o") || c.startsWith("i") || c.startsWith("l") || c.startsWith("f") || c.startsWith("m") || c.startsWith("d")) {
                        identifier = c
                    }
                })
            }
        } else if (isNumber) {
            type = "number"
            identifier = el.closest("g").getAttribute("class")
        } else if (isRoot) {
            type = "root"
            identifier = getRootId(el)
        } else if (isSide) {
            type = "side"
            const surfaceClasses = el.classList;
            [...surfaceClasses].forEach(c => {
                if (c.startsWith("t")) {
                    identifier = c
                }
            })
        }

        // Every posterior tooth's top view also contains an un-numbered cls-2
        // detail path. Without this, clicking one is reported as a surface hit
        // with no identifier, which lands an { id: undefined } entry in the
        // selection and then in the saved procedure.
        if (type !== "none" && identifier) {
            const res = {
                event: e,
                type,
                id: identifier,
                style: "selection",
                description: ""
            }
            if (!isClick) {
                res.valid = true;
            }
            try {
                let justTheNo = identifier.replace(/\D/g, '').trim();
                res['description'] = teethDescription()[justTheNo + ""]
                if (type == "surface") {
                    let designation = identifier.replace(/\d/g, '').trim()
                    res['symbol'] = designation
                    res['description'] += " - " + topViewDescription[designation + ""]
                }

                res['id'] = justTheNo
            } catch (e) {
                console.error(e)
            }
            callBack(res)
        } else {
            if (!isClick)
                callBack({
                    valid: false
                })
        }
    }

    const handleDiagramClick = (e) => {
        if (onClick)
            handleEvent(e, onClick, true)
    }

    const handleDiagramMouseLeave = (e) => {
        if (onMouseLeave)
            handleEvent(e, onMouseLeave)
    }

    const handleDiagramMouseEnter = (e) => {
        if (onMouseEnter)
            handleEvent(e, onMouseEnter)
    }
    const handleDiagramMouseMove = (e) => {
        if (onMouseMove)
            handleEvent(e, onMouseMove)
    }

    return (<TeethDiagram ref={diagramRef}
        isFdi={isFdi}
        dentition={dentition}
        mouseLeaveHandler={handleDiagramMouseLeave}
        mouseEnterHandler={handleDiagramMouseEnter}
        mouseMoveHandler={handleDiagramMouseMove}
        clickHandler={handleDiagramClick}
    />)
}